import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { PublicUser } from '@amg/protocol';
import { api } from './api';

interface AuthValue {
  user: PublicUser | null;
  /** True until the first `/api/auth/me` answer arrives. */
  loading: boolean;
  signIn(email: string, password: string): Promise<PublicUser>;
  signOut(): Promise<void>;
  changePassword(currentPassword: string, newPassword: string): Promise<PublicUser>;
  refresh(): Promise<void>;
}

const AuthContext = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<PublicUser | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const res = await api<{ user: PublicUser | null }>('/api/auth/me');
      setUser(res.user);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const value = useMemo<AuthValue>(
    () => ({
      user,
      loading,
      refresh,
      async signIn(email, password) {
        const res = await api<{ user: PublicUser }>('/api/auth/login', { body: { email, password } });
        setUser(res.user);
        return res.user;
      },
      async signOut() {
        await api('/api/auth/logout', { method: 'POST', body: {} });
        setUser(null);
      },
      async changePassword(currentPassword, newPassword) {
        const res = await api<{ user: PublicUser }>('/api/auth/password', { body: { currentPassword, newPassword } });
        setUser(res.user);
        return res.user;
      },
    }),
    [user, loading, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth must be used inside <AuthProvider>');
  return value;
}
